import { tenantApi } from '@shared/api/tenantApi';

/**
 * Surveys & eForms API — `/survey-responses/*` member-facing routes
 * (verified against tenant-api `routes/index.ts`). Responses use the
 * `{ success, data }` envelope; `.data` is unwrapped here.
 */

/** Author-configured respondent messages, stored on survey_template.display_context. */
export interface SurveyDisplayContext {
  intro_message?: { html?: string } | null;
  completion_message?: { html?: string } | null;
  [key: string]: unknown;
}

export interface SurveyView {
  survey_template_id: string;
  flow_template_id: string;
  flow_template_name?: string;
  name: string;
  description?: string | null;
  flow_type: string;
  publication_status: string;
  deadline_at?: string | null;
  display_context?: SurveyDisplayContext | null;
  /** Whether each member is limited to a single submission (defaults to true when unset). */
  response_policy?: { one_per_member?: boolean } | null;
}

export interface MyResponse {
  survey_instance_id: string;
  flow_instance_id: string;
  /** The runner needs it on every call on `flow_instance_id`. */
  flow_instance_token: string;
  survey_template_id: string;
  submitted_at?: string | null;
  submission_source: string;
  created_at: string;
  name: string;
  description: string | null;
  flow_type: string;
  deadline_at: string | null;
  /** Reachable steps on this response's path; skipped branches excluded. */
  total_steps?: number;
  completed_steps?: number;
}

export interface SurveyResponse {
  survey_instance_id: string;
  flow_instance_id: string;
  /** The runner needs it on every call on `flow_instance_id`. */
  flow_instance_token: string;
  survey_template_id: string;
  submitted_at?: string | null;
  submission_source: string;
  created_at?: string;
}

export interface SubmissionAnswer {
  content_key?: string | null;
  item_key?: string | null;
  name?: string | null;
  question_type?: string | null;
  item_value?: unknown;
  sequence_number?: number | null;
  step_name?: string | null;
  step_sequence?: number | null;
}

export interface SubmittedResponseDetail extends Omit<MyResponse, 'flow_instance_token'> {
  respondent_member_id?: string | null;
  answers?: SubmissionAnswer[];
  display_context?: SurveyDisplayContext | null;
}

interface Envelope<T> {
  success?: boolean;
  data: T;
}

export async function fetchAvailableSurveys(): Promise<SurveyView[]> {
  const { data } = await tenantApi.get<Envelope<SurveyView[]>>('/survey-responses/available');
  return data.data ?? [];
}

export async function fetchResidentSurvey(id: string): Promise<SurveyView> {
  const { data } = await tenantApi.get<Envelope<SurveyView>>(
    `/survey-responses/available/${id}`,
  );
  return data.data;
}

export async function fetchMyResponses(): Promise<MyResponse[]> {
  const { data } = await tenantApi.get<Envelope<MyResponse[]>>('/survey-responses/my');
  return data.data ?? [];
}

export async function fetchMyResponseDetail(surveyInstanceId: string): Promise<SubmittedResponseDetail> {
  const { data } = await tenantApi.get<Envelope<SubmittedResponseDetail>>(
    `/survey-responses/my/${surveyInstanceId}`,
  );
  return data.data;
}

export interface SubmitSurveyPayload {
  survey_template_id: string;
  submission_source?: 'portal' | 'public_link' | 'ccs' | 'mobile';
  instance_data?: Record<string, unknown>;
}

export async function submitSurveyResponse(payload: SubmitSurveyPayload): Promise<SurveyResponse> {
  const { data } = await tenantApi.post<Envelope<SurveyResponse>>('/survey-responses', {
    submission_source: 'mobile',
    ...payload,
  });
  return data.data;
}

